'use client';
import React, { useEffect, useRef, useState } from 'react';

const features = [
  {
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
        <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke="#131c15" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    title: 'State-of-the-Art Equipment',
    desc: 'We use the latest advancements in dental technology to provide precise diagnoses and superior treatment outcomes.',
    highlight: false,
  },
  {
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
        <path d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" stroke="#131c15" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    title: 'Expert & Caring Specialist',
    desc: 'Dr. Abhishek Pal brings 5+ years of specialized expertise in prosthodontics and implantology with a passion for care.',
    highlight: false,
  },
  {
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
        <path d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" stroke="#131c15" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    title: 'Pain-Free, Stress-Free Treatments',
    desc: 'We understand dental anxiety. Our gentle approach and modern techniques ensure every visit is comfortable and calm.',
    highlight: true,
  },
  {
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
        <path d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" stroke="#131c15" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    title: 'Personalized Treatment Plans',
    desc: 'Every smile is unique. We craft individualized treatment plans tailored to your specific dental needs and aesthetic vision.',
    highlight: false,
  },
];

export default function About() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref?.current) observer?.observe(ref?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section id="about" ref={ref} className="bg-white py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Section Label */}
        <div
          className={`inline-flex items-center gap-2 bg-secondary rounded-full px-4 py-1.5 mb-6 transition-all duration-500 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          <span className="font-inter text-xs font-semibold text-dark/60 uppercase tracking-widest">Why Choose Us</span>
        </div>

        {/* Two-column header */}
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-20 mb-14 items-end">
          <h2
            className={`font-unbounded font-black text-dark text-3xl lg:text-4xl xl:text-5xl leading-[1.08] transition-all duration-600 delay-100 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            About DrPal's
            <br />
            Dental Clinic
          </h2>
          <div
            className={`transition-all duration-600 delay-200 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            <p className="font-inter text-dark/55 text-base lg:text-lg leading-relaxed mb-4">
              At DrPal's Dentistry, we believe a healthy smile is a gateway to confidence and well-being.
              Whether you need cosmetic enhancements, full mouth rehabilitation, or advanced implant solutions,
              we are here to help you achieve the smile of your dreams.
            </p>
            <p className="font-inter text-dark/55 text-sm leading-relaxed">
              Dr. Abhishek Pal, a qualified Prosthodontist and Implantologist, combines clinical excellence
              with a warm, patient-first approach to deliver outstanding results.
            </p>
          </div>
        </div>

        {/* Feature Cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {features?.map((feature, i) => (
            <div
              key={feature?.title}
              className={`rounded-3xl p-6 transition-all duration-500 hover:shadow-card-hover hover:-translate-y-1 cursor-default ${
                feature?.highlight
                  ? 'bg-primary shadow-teal'
                  : 'bg-secondary hover:bg-primary/20'
              } ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: `${200 + i * 100}ms` }}
            >
              <div
                className={`w-11 h-11 rounded-2xl flex items-center justify-center mb-5 ${
                  feature?.highlight ? 'bg-white/60' : 'bg-white shadow-soft'
                }`}
              >
                {feature?.icon}
              </div>
              <h3 className="font-unbounded font-bold text-dark text-sm leading-snug mb-3">
                {feature?.title}
              </h3>
              <p className={`font-inter text-xs leading-relaxed ${feature?.highlight ? 'text-dark/65' : 'text-dark/55'}`}>
                {feature?.desc}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
